import { DisclaimerAssumptionsButton } from "@components/buttons/disclaimer-assumptions";
import { PlusButton } from "@components/buttons/plus";
import {
  SHAlert,
  SHBox,
  SHCheckbox,
  SHContainer,
  SHDivider,
  SHFormControlLabel,
  SHNumericField,
  SHStack,
  SHToggleButtonOptions,
  SHTypography,
} from "@components/design-systems";
import { FeeCalculatorDisclaimerDialog } from "@components/dialogs/fee-calculator";
import { WarningSVG } from "@components/svgs/warning";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNotification } from "@hooks/useNotification";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { FieldGroup } from "@layouts/form/field-group";
import { StepCompProps, StepCompRef } from "@layouts/stepper";
import { StepName } from "@layouts/stepper/step-name";
import { PortfolioInvestmentTypeSelection } from "@models/managed-accounts/entities/step/setup";
import { DisclaimerDTO } from "@models/platform-analysis/entities/disclaimer";
import {
  AnalysisFeeCalculatorDTO,
  FamilyMemberDTO,
  FeeCalculatorDTO,
} from "@models/platform-analysis/entities/steps/fee";
import { FeeCalculatorFieldType } from "@models/platform-analysis/enums/fee/fieldType";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { AnalysisSubStep } from "@models/platform-analysis/enums/subStep";
import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import { Collapse, useTheme } from "@mui/material";
import { PortfolioSetupConstants } from "@pages/platform-analysis/_id/steps/fee/calculator/components/portfolio-setup/constants";
import { AnalysisFeePortfolioSetupSection } from "@pages/platform-analysis/_id/steps/fee/calculator/components/portfolio-setup/section";
import { NoBalancesDialog } from "@pages/platform-analysis/components/dialogs/no-balances";
import { FeeCalculatorPortfolioLevelOptions } from "@pages/platform-analysis/constant";
import { RootState } from "@redux/store";
import { debounce, round } from "lodash";
import {
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  Controller,
  FieldPath,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { Control } from "react-hook-form/dist/types";
import { NumericFormat } from "react-number-format";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { TransitionGroup } from "react-transition-group";
import { useEffectOnce, useToggle } from "react-use";
import { AutoCalculatedField } from "./components/auto-calculated-field";
import { FeeCurrencyField } from "./components/currency-field";
import { FamilyCard } from "./components/family-card";
import { FamilyCardTotal } from "./components/family-card-total";
import InvalidPortfolioWarningDialog from "./components/invalid-portfolio-warning-dialog";
import {
  getPortfolioSetupFieldPath,
  portfolioInvestmentTypeCheckboxValidation,
} from "./components/portfolio-setup/utils";
import {
  AcrossAllAccountsBalances,
  ChooseOptions,
  clearDataConditions,
  MaxTotalAccount,
  MinAccountBalance,
} from "./config";
import { AnalysisFeeCalculatorSkeleton } from "./skeleton";
import {
  calculateTotals,
  checkHasEmptyBalance,
  checkValidPortfolioSetup,
  feeCalculatorValidators,
  filterPortfoliosInUse,
  getCFSAndNorthVariablesResetConditions,
  getIndexMapping,
  getLabelMapping,
} from "./util";

export interface FeeCalculatorStepProps extends StepCompProps {}

export const FeeCalculatorStep: ForwardRefRenderFunction<
  StepCompRef,
  FeeCalculatorStepProps
> = ({ stepNumber }: FeeCalculatorStepProps, ref) => {
  const { palette } = useTheme();
  const [confirmDialog, setConfirmDialog] = useState<
    | {
        callback: (value: boolean | PromiseLike<boolean>) => void;
      }
    | undefined
  >(undefined);
  const [fetchedData, setFetchedData] = useState(false);
  const [disclaimers, setDisclaimers] = useState<DisclaimerDTO[]>();

  const { analysisId } = useParams<{ analysisId: string }>();
  const {
    setIsDirty,
    setIsValid,
    setFeeCalculator,
    setShowSaveProgressWarning,
    setIsValidPortfolioSetups,
    loadFeeCalculator,
    loadPlatformAnalysisDisclaimers,
    updateFeeCalculator,
  } = usePlatformAnalysis();
  const {
    ui: { isLoading, isSubmitting, isDirty, isValid },
    isReadOnly,
    feeCalculator,
  } = useSelector((state: RootState) => state.platformAnalysis);
  const { notify } = useNotification();
  const formMethod = useForm({
    mode: "onChange",
    defaultValues: { ...new AnalysisFeeCalculatorDTO() },
    resolver: yupResolver(feeCalculatorValidators),
  });
  const {
    reset,
    getValues,
    watch,
    control,
    setValue,
    formState: { isValid: formValid, isDirty: formDirty },
  } = formMethod;

  const {
    fields: familyMembersFields,
    append: appendFamilyMember,
    remove: removeFamilyMember,
  } = useFieldArray({
    control,
    name: "familyMembers",
    keyName: "fieldId",
  });
  const watchedFeeCalculators = watch("feeCalculators") || [];
  const indexMapping = getIndexMapping(watchedFeeCalculators);
  const labelMapping = getLabelMapping(watchedFeeCalculators);

  const nameOf = (variableName: AnalysisVariable) => {
    const field = `feeCalculators.${indexMapping.get(
      variableName,
    )}` as FieldPath<AnalysisFeeCalculatorDTO>;
    const value =
      `${field}.feeCalculatorData.value` as FieldPath<AnalysisFeeCalculatorDTO>;

    return { field, value };
  };

  const watchedPortfolioLevel = watch(
    nameOf(AnalysisVariable.Portfolio_setup).value,
  );
  const isNoPortfolioLevel =
    watchedPortfolioLevel === FeeCalculatorPortfolioLevel.No;

  const watchField = (name: AnalysisVariable) =>
    watch(nameOf(name).field) as FeeCalculatorDTO;

  const watchBooleanValue = (name: AnalysisVariable) =>
    watch(nameOf(name).value) === "true" || false;

  const watchNumberValue = (name: AnalysisVariable) => {
    const strValue = watch(nameOf(name).value);
    if (!strValue) return 0;
    try {
      return Number(strValue);
    } catch (err) {
      return 0;
    }
  };

  const isChooseMF = watchBooleanValue(AnalysisVariable.Choose_MF);
  const isChooseAU = watchBooleanValue(AnalysisVariable.Choose_AU);
  const isChooseIntl = watchBooleanValue(AnalysisVariable.Choose_Intl);
  const isChooseMA = watchBooleanValue(AnalysisVariable.Choose_MA);
  const isChooseTD = watchBooleanValue(AnalysisVariable.Choose_TD);
  const isChooseRetIns = watchBooleanValue(AnalysisVariable.Choose_ret_ins);
  const isChooseBond = watchBooleanValue(AnalysisVariable.Choose_bond);

  const isShowHoldingsNumber = watch("isShowHoldingsNumber");

  const isShowCFSEdgeSection =
    watch("containsCfsEdge") &&
    isNoPortfolioLevel &&
    (isChooseMF || isChooseMA);
  const isShowCFSEdgeEligibleMFBalance = isShowCFSEdgeSection && isChooseMF;
  const isShowCFSEdgeA100PortfolioBalance = isShowCFSEdgeSection && isChooseMA;
  const isShowCFSEdgeA30PortfolioBalance = isShowCFSEdgeA100PortfolioBalance;

  const isShowNorthSection =
    watch("containsNorth") && isNoPortfolioLevel && (isChooseMF || isChooseMA);
  const isShowNorthSelectBalance = isShowNorthSection;
  const isShowNorthGrowBalance = isShowNorthSection && isChooseMA;

  const [showFeeDialog, setShowFeeDialog] = useState(false);
  const calculatedTotal = calculateTotals(watch("familyMembers"));
  const portfolioSetups = watch("portfolioSetups");
  const portfolios = watch("portfolios");

  const [
    isOpenInvalidPortfolioWarningDialog,
    toggleIsOpenInvalidPortfolioWarningDialog,
  ] = useToggle(false);

  const isValidAccountBalance = calculatedTotal.total > MinAccountBalance;
  const isValidAccountCount = calculatedTotal.account <= MaxTotalAccount;

  const hasIDPS = useMemo(
    () => calculatedTotal.idps > 0,
    [calculatedTotal.idps],
  );
  const hasSuper = useMemo(
    () => calculatedTotal.super > 0,
    [calculatedTotal.super],
  );
  const hasRecalculate = useMemo(
    () => feeCalculator?.hasRecalculate ?? false,
    [feeCalculator?.hasRecalculate],
  );

  const idpsCashBalance = hasIDPS
    ? watchNumberValue(AnalysisVariable.IDPS_Cash_balance)
    : 0;
  const superCashBalance = hasSuper
    ? watchNumberValue(AnalysisVariable.Super_Cash_balance)
    : 0;
  const mfBalanceI = isChooseMF
    ? watchNumberValue(AnalysisVariable.MF_balance_i)
    : 0;
  const auBalanceI = isChooseAU
    ? watchNumberValue(AnalysisVariable.AU_balance_i)
    : 0;
  const intlBalanceI = isChooseIntl
    ? watchNumberValue(AnalysisVariable.Intl_balance_i)
    : 0;
  const maBalanceI = isChooseMA
    ? watchNumberValue(AnalysisVariable.MA_balance_i)
    : 0;
  const bondBalanceI = isChooseBond
    ? watchNumberValue(AnalysisVariable.Bond_balance_i)
    : 0;
  const tdBalanceI = isChooseTD
    ? watchNumberValue(AnalysisVariable.TD_balance_i)
    : 0;
  const edgeA100PortfolioBalance = watchNumberValue(
    AnalysisVariable.Edge_A100_Portfolio_balance,
  );
  const edgeA30PortfolioBalance = watchNumberValue(
    AnalysisVariable.Edge_A30_Portfolio_balance,
  );
  const edgeEligibleMFBalance = watchNumberValue(
    AnalysisVariable.Edge_eligible_MF_balance,
  );
  const northSelectBalance = watchNumberValue(
    AnalysisVariable.NorthSelect_balance,
  );
  const northGrowBalance = watchNumberValue(
    AnalysisVariable.North_Grow_Portfolio_balance,
  );

  const totalBalanceInvestmentClassSection = useMemo(
    () =>
      idpsCashBalance +
      superCashBalance +
      mfBalanceI +
      auBalanceI +
      intlBalanceI +
      maBalanceI +
      bondBalanceI +
      tdBalanceI,
    [
      idpsCashBalance,
      superCashBalance,
      mfBalanceI,
      auBalanceI,
      intlBalanceI,
      maBalanceI,
      bondBalanceI,
      tdBalanceI,
    ],
  );

  const totalCash = useMemo(
    () => idpsCashBalance + superCashBalance,
    [idpsCashBalance, superCashBalance],
  );

  const totalCashAndTD = useMemo(
    () => idpsCashBalance + superCashBalance + tdBalanceI,
    [idpsCashBalance, superCashBalance, tdBalanceI],
  );

  const fullMenuBalance = useMemo(
    () =>
      calculatedTotal.total -
      totalCash -
      edgeA100PortfolioBalance -
      edgeA30PortfolioBalance -
      edgeEligibleMFBalance,
    [
      calculatedTotal.total,
      totalCash,
      edgeA100PortfolioBalance,
      edgeA30PortfolioBalance,
      edgeEligibleMFBalance,
    ],
  );
  const choiceMenuBalance = useMemo(
    () =>
      calculatedTotal.total -
      totalCashAndTD -
      northSelectBalance -
      northGrowBalance,
    [
      calculatedTotal.total,
      totalCashAndTD,
      northSelectBalance,
      northGrowBalance,
    ],
  );

  const isValidTotal = useMemo(
    () =>
      round(totalBalanceInvestmentClassSection, 2) <=
      round(calculatedTotal.total, 2),
    [totalBalanceInvestmentClassSection, calculatedTotal.total],
  );
  const isValidIDPS = useMemo(
    () => idpsCashBalance === 0 || idpsCashBalance < calculatedTotal.idps,
    [idpsCashBalance, calculatedTotal.idps],
  );
  const isValidSuper = useMemo(
    () => superCashBalance === 0 || superCashBalance < calculatedTotal.super,
    [superCashBalance, calculatedTotal.super],
  );
  const isValidFullMenuBalance = useMemo(
    () => fullMenuBalance >= 0,
    [fullMenuBalance],
  );
  const isValidEdgeEligibleMFBalance = useMemo(
    () => edgeEligibleMFBalance <= mfBalanceI,
    [edgeEligibleMFBalance, mfBalanceI],
  );
  const isValidEdeTotalA100A30Balance = useMemo(
    () => edgeA100PortfolioBalance + edgeA30PortfolioBalance <= maBalanceI,
    [edgeA100PortfolioBalance, edgeA30PortfolioBalance, maBalanceI],
  );
  const isValidChoiceMenuBalance = useMemo(
    () => choiceMenuBalance >= 0,
    [choiceMenuBalance],
  );
  const isValidNorthSelectBalance = useMemo(
    () => northSelectBalance <= mfBalanceI + maBalanceI,
    [northSelectBalance, mfBalanceI, maBalanceI],
  );

  const isCanAddNewFamilyMember = useMemo(
    () => familyMembersFields.length < MaxTotalAccount,
    [familyMembersFields.length],
  );
  const isDisabled = useMemo(
    () => isSubmitting || isLoading || isReadOnly,
    [isSubmitting, isLoading, isReadOnly],
  );

  const showInvestmentClassSection = useMemo(() => {
    const basicConditions = [
      hasIDPS,
      hasSuper,
      isChooseMF,
      isChooseMA,
      isChooseAU,
      isChooseIntl,
      isChooseBond,
    ].some(Boolean);

    return isNoPortfolioLevel && basicConditions;
  }, [
    isNoPortfolioLevel,
    hasIDPS,
    hasSuper,
    isChooseAU,
    isChooseBond,
    isChooseIntl,
    isChooseMA,
    isChooseMF,
  ]);

  const showTransactionOutsideSection = useMemo(
    () => isChooseMF || isChooseAU || isChooseIntl || isChooseBond,
    [isChooseMF, isChooseAU, isChooseIntl, isChooseBond],
  );

  const showTransactionWithinSection = useMemo(
    () => isChooseMA && isNoPortfolioLevel,
    [isChooseMA, isNoPortfolioLevel],
  );

  const investmentTypeSelection = useMemo(() => {
    return {
      isChooseMF,
      isChooseAU,
      isChooseIntl,
      isChooseBond,
      isChooseTD,
    } as PortfolioInvestmentTypeSelection;
  }, [isChooseMF, isChooseAU, isChooseIntl, isChooseBond, isChooseTD]);

  useImperativeHandle(ref, () => ({
    onChangeStep: async (newStep) => {
      if (isLoading) return true;
      const isBack =
        !newStep?.key ||
        (newStep?.key &&
          ["BusinessMetric", "Feature", "Setup"].includes(newStep?.key));

      const isNext = newStep?.key === AnalysisSubStep.Analysis;
      const isValidPortfolioSetups = isNoPortfolioLevel
        ? true
        : checkValidPortfolioSetup(portfolioSetups || []);
      const shouldSubmit = ((isBack && isValid) || isNext) && isDirty;

      if (isBack && shouldSubmit && !isValidPortfolioSetups) {
        setShowSaveProgressWarning(true);
        return false;
      }

      if (!shouldSubmit) {
        if (isBack) return true;
        if (isNext && isValidPortfolioSetups) return true;
      }

      return await handleOnSubmit();
    },
    onChangeOtherStep: async () => {
      if (isLoading) return true;
      const shouldSubmit = isValid && isDirty;

      if (!shouldSubmit) return true;

      const isValidPortfolioSetups = isNoPortfolioLevel
        ? true
        : checkValidPortfolioSetup(portfolioSetups || []);

      if (shouldSubmit && !isValidPortfolioSetups) {
        setShowSaveProgressWarning(true);
        return false;
      }

      return await handleOnSubmit();
    },
    onSubmit: async () => {
      return await handleOnSubmit();
    },
  }));

  const getConfirm = async (): Promise<boolean> => {
    return await new Promise((res) => {
      setConfirmDialog({ callback: res });
    });
  };

  const handleOnSubmit = async () => {
    if (!analysisId) return false;
    const formData = getValues();
    const isHasEmptyBalance = checkHasEmptyBalance(formData.familyMembers);
    if (isHasEmptyBalance) {
      const isContinue = await getConfirm();
      setConfirmDialog(undefined);
      if (!isContinue) {
        return false;
      }
    }

    const isValidPortfolioSetups = checkValidPortfolioSetup(
      portfolioSetups || [],
    );
    if (!isValidPortfolioSetups && !isNoPortfolioLevel) {
      toggleIsOpenInvalidPortfolioWarningDialog();
      return false;
    }

    const isSuccess = await updateFeeCalculator({
      platformAnalysisId: analysisId,
      formData,
    });
    if (isSuccess) {
      let clone: AnalysisFeeCalculatorDTO = { ...formData };
      //Filter empty balance
      clone.familyMembers = clone.familyMembers
        .filter((member) => member.totalBalance > 0)
        .map((member) => ({
          ...member,
          familyMemberAccounts: member.familyMemberAccounts.filter(
            (account) => account.balance > 0,
          ),
        }));
      clone.portfolios = filterPortfoliosInUse({
        data: clone,
        portfolioLevel: watchedPortfolioLevel as FeeCalculatorPortfolioLevel,
      });

      reset(clone);
      if (feeCalculator) {
        setFeeCalculator({ ...clone, hasRecalculate: false });
      }
    }
    return isSuccess;
  };

  const handleOnAddFamilyEntity = () => {
    appendFamilyMember(new FamilyMemberDTO());
  };
  const handleOnChangeFamilyEntity = (
    index: number,
    state: FamilyMemberDTO,
  ) => {
    let entity = { ...state };
    setValue(`familyMembers.${index}`, entity, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };
  const handleOnRemoveFamilyEntity = (index: number) => {
    removeFamilyMember(index);
  };

  useEffect(() => {
    setIsDirty(formDirty || hasRecalculate);
    // eslint-disable-next-line
  }, [formDirty, hasRecalculate]);

  useEffect(() => {
    const baseValidations = [
      formValid,
      isValidAccountBalance,
      isValidAccountCount,
    ];

    const portfolioLevelValidations = [
      isValidTotal,
      isValidIDPS,
      isValidSuper,
      isValidFullMenuBalance,
      isValidEdgeEligibleMFBalance,
      isValidEdeTotalA100A30Balance,
      isValidChoiceMenuBalance,
      isValidNorthSelectBalance,
    ];

    const allValidations = isNoPortfolioLevel
      ? [...baseValidations, ...portfolioLevelValidations]
      : baseValidations;

    setIsValid(allValidations.every(Boolean));

    // eslint-disable-next-line
  }, [
    isNoPortfolioLevel,
    formValid,
    isValidAccountBalance,
    isValidAccountCount,
    isValidTotal,
    isValidIDPS,
    isValidSuper,
    isValidFullMenuBalance,
    isValidEdgeEligibleMFBalance,
    isValidEdeTotalA100A30Balance,
    isValidChoiceMenuBalance,
    isValidNorthSelectBalance,
  ]);

  useEffect(() => {
    if (!hasIDPS) {
      setValue(nameOf(AnalysisVariable.IDPS_Cash_balance).value, "0", {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    // eslint-disable-next-line
  }, [hasIDPS]);

  useEffect(() => {
    if (!hasSuper) {
      setValue(nameOf(AnalysisVariable.Super_Cash_balance).value, "0", {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    // eslint-disable-next-line
  }, [hasSuper]);

  useEffectOnce(() => {
    return () => {
      setIsValid(true);
      setIsDirty(false);
    };
  });

  useEffect(() => {
    setIsValidPortfolioSetups(
      isNoPortfolioLevel
        ? true
        : checkValidPortfolioSetup(portfolioSetups || []),
    );
    // eslint-disable-next-line
  }, [portfolioSetups, isNoPortfolioLevel]);

  const resetPortfolioSetupSection = useMemo(
    () =>
      debounce(() => {
        portfolioSetups?.forEach((_, index) => {
          const fieldPaths = getPortfolioSetupFieldPath(index);
          setValue(fieldPaths.selectedPortfolioId, null, {
            shouldDirty: false,
            shouldValidate: true,
          });
          setValue(fieldPaths.hasAllocationError, false);
          setValue(fieldPaths.isMissingInvestment, false);
          setValue(fieldPaths.missingInvestmentTooltip, null);
          setValue(fieldPaths.isMissingInvestmentOption, false);
          setValue(fieldPaths.missingInvestmentOptionTooltip, null);
          setValue(fieldPaths.isBelowMinimumInvestmentSize, false);
          setValue(fieldPaths.belowMinimumInvestmentSizeTooltip, null);
          setValue(fieldPaths.hasUnavailableInvestmentOption, false);
          setValue(fieldPaths.unavailableInvestmentOptionTooltip, null);
          setValue(fieldPaths.hasUnavailableInvestment, false);
          setValue(fieldPaths.unavailableInvestmentTooltip, null);
          setValue(fieldPaths.hasCashNotAvailableInSuperError, false);
          setValue(fieldPaths.hasCashNotAvailableInIDPSError, false);
        });
      }, PortfolioSetupConstants.DefaultDelayTimeMs),
    [portfolioSetups, setValue],
  );

  useEffect(() => {
    if (watchedPortfolioLevel === FeeCalculatorPortfolioLevel.No) {
      resetPortfolioSetupSection();
      return;
    }

    if (watchedPortfolioLevel === FeeCalculatorPortfolioLevel.Family) {
      ChooseOptions.forEach((variable) => {
        const notExistingPortfolioWeight =
          portfolioInvestmentTypeCheckboxValidation(
            variable,
            watchedPortfolioLevel as FeeCalculatorPortfolioLevel,
            portfolios,
          );

        if (!notExistingPortfolioWeight)
          setValue(nameOf(variable).value, "true");
      });

      return;
    }
    // eslint-disable-next-line
  }, [watchedPortfolioLevel]);

  const loadDisclaimers = async () => {
    if (analysisId && analysisId !== "new") {
      const disclaimers = await loadPlatformAnalysisDisclaimers(
        analysisId ?? "",
      );
      setDisclaimers(disclaimers);
    }
  };

  const loadData = async (analysisId?: string) => {
    if (!analysisId || analysisId === "new") return;
    setFetchedData(false);
    // const newSteps = await getPlatformAnalysisAllSteps(analysisId);
    const feeCalculator = await loadFeeCalculator({
      platformAnalysisId: analysisId,
    });
    setFetchedData(true);
    if (!feeCalculator) return;
    // setIsReachedFeeStep(
    //   some(newSteps?.data?.savedSteps, (item) => item === AnalysisStep.Fee),
    // );
    if (!feeCalculator.familyMembers.length) {
      //Add a family member when in new mode
      return reset({
        ...feeCalculator,
        familyMembers: [new FamilyMemberDTO()],
      });
    }
    reset(feeCalculator);
  };

  const resetBalanceFields = () => {
    AcrossAllAccountsBalances.forEach((variable) => {
      setValue(nameOf(variable).value, "0");
    });
  };

  useEffect(() => {
    loadDisclaimers();
    loadData(analysisId);
    // eslint-disable-next-line
  }, [analysisId]);

  useEffect(() => {
    if (!isNoPortfolioLevel) {
      setValue(nameOf(AnalysisVariable.Choose_MA).value, "true");
    }
    // eslint-disable-next-line
  }, [isNoPortfolioLevel]);

  useEffect(() => {
    const fieldsToReset = getCFSAndNorthVariablesResetConditions({
      isShowCFSEdgeEligibleMFBalance,
      isShowCFSEdgeA100PortfolioBalance,
      isShowCFSEdgeA30PortfolioBalance,
      isShowNorthSelectBalance,
      isShowNorthGrowBalance,
    });

    fieldsToReset.forEach(({ condition, variable }) => {
      condition && setValue(nameOf(variable).value, "0");
    });
    // eslint-disable-next-line
  }, [
    isShowCFSEdgeEligibleMFBalance,
    isShowCFSEdgeA100PortfolioBalance,
    isShowCFSEdgeA30PortfolioBalance,
    isShowNorthSelectBalance,
    isShowNorthGrowBalance,
  ]);

  useEffect(() => {
    setValue(
      nameOf(AnalysisVariable.EssentialPlus_TD_balance).value,
      String(tdBalanceI),
    );
    // eslint-disable-next-line
  }, [tdBalanceI]);

  useEffect(() => {
    setValue(
      nameOf(AnalysisVariable.EssentialPlus_ETF_balance).value,
      String(auBalanceI),
    );
    // eslint-disable-next-line
  }, [auBalanceI]);

  if (isLoading) return <AnalysisFeeCalculatorSkeleton />;
  return (
    <FormProvider {...formMethod}>
      <form>
        <SHContainer sx={{ px: { xs: "16px", lg: 0 }, py: "25px" }}>
          <StepName name={"Fee calculator"} stepNumber={stepNumber} />
          <SHStack>
            <SHStack spacing={"15px"} py="25px">
              <SHTypography variant="subtitle1">Welcome! </SHTypography>
              <SHTypography variant="body4">
                You can fill as much or as little of the fee calculator as you
                like. We will use the information you provide to simulate an
                estimated cost for each platform. Most fields are not
                compulsory, but the more you fill out, the more comprehensive
                our fee estimates will be.
              </SHTypography>
              <SHTypography variant="body4" fontSize={"10px"}>
                Any result provided by this calculator is illustrative only. It
                relies upon information and assumptions that are subject to
                change. If varied, this information and assumptions may change
                the result. Any result is not intended to be relied on for the
                purpose of making a decision about a particular financial
                product, platform or provider.
              </SHTypography>
              <SHTypography variant="body4" fontSize={"10px"}>
                By using this calculator, you confirm that you have read and
                accepted the {"  "}
                <DisclaimerAssumptionsButton
                  onClick={() => setShowFeeDialog(true)}
                />
              </SHTypography>
            </SHStack>
            <SHDivider />
            <FieldGroup
              title="What do you expect the average holding balance of each account will be for the next 12 months? (Maximum 6 accounts)"
              subTitle={`<p>Please enter the approximate value, and consider any deposits and withdrawals you expect in the coming year.</p><br/><p>Note: The fee calculator currently supports a maximum of 6 accounts.</p>`}
              maxWidth={490}
            >
              <SHStack
                spacing={3}
                direction="column"
                sx={{ width: { xs: "100%", md: 520 } }}
              >
                <TransitionGroup
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: "25px",
                    width: "100%",
                  }}
                >
                  {familyMembersFields.map((entity, index) => (
                    <Collapse key={entity.id}>
                      <FamilyCard
                        order={index + 1}
                        disabled={isDisabled}
                        canRemove={index !== 0}
                        familyMember={watch(`familyMembers.${index}`)}
                        disabledAddButton={!isValidAccountCount}
                        onChange={(state) =>
                          handleOnChangeFamilyEntity(index, state)
                        }
                        onRemove={() => handleOnRemoveFamilyEntity(index)}
                      />
                    </Collapse>
                  ))}
                </TransitionGroup>
                <SHStack alignItems={"center"}>
                  <PlusButton
                    onClick={handleOnAddFamilyEntity}
                    disabled={
                      isDisabled ||
                      !isValidAccountCount ||
                      !isCanAddNewFamilyMember
                    }
                  >
                    Family member/entity
                  </PlusButton>
                </SHStack>
                <SHStack spacing={"5px"}>
                  <Collapse in={!isValidAccountBalance && fetchedData}>
                    <SHAlert
                      severity="error"
                      textAlign={"left"}
                      icon={<WarningSVG />}
                    >
                      At least one account must have a balance
                    </SHAlert>
                  </Collapse>
                  <Collapse in={!isValidAccountCount && fetchedData}>
                    <SHAlert
                      severity="error"
                      textAlign={"left"}
                      icon={<WarningSVG />}
                    >
                      Maximum 6 accounts supported. Please remove one
                    </SHAlert>
                  </Collapse>
                  <FamilyCardTotal
                    title={"Total value of investments and super"}
                    content={
                      <>
                        $
                        {(() => {
                          const value = Number(
                            calculatedTotal.total.toFixed(2),
                          );
                          const showDecimalPlacesForValue = value % 1 !== 0;
                          return (
                            <NumericFormat
                              displayType="text"
                              defaultValue={0}
                              value={value}
                              thousandSeparator=","
                              decimalScale={showDecimalPlacesForValue ? 2 : 0}
                              fixedDecimalScale={showDecimalPlacesForValue}
                            />
                          );
                        })()}
                      </>
                    }
                  />
                </SHStack>
              </SHStack>
            </FieldGroup>
            <SHDivider />

            <FieldGroup
              title="Include a portfolio of SMAs in the fee analysis?"
              subTitle={`<p>
                Choose whether to factor in a portfolio of SMAs within the fee
                analysis. Including the portfolio means the disclosed fees of
                SMAs will be added to the total platform cost. You may enter the
                portfolio either at the <strong><u>family group level</u></strong> or at the individual
                <strong><u>account level</u></strong>, depending on your preferred scope of analysis.
              </p>`}
              maxWidth={490}
            >
              <SHStack
                width={"100%"}
                alignItems={"start"}
                sx={{ width: { xs: "100%", md: 520 } }}
              >
                <Controller
                  control={control}
                  name={nameOf(AnalysisVariable.Portfolio_setup).value}
                  render={({ field: { ref, ...others } }) => {
                    return (
                      <SHToggleButtonOptions
                        {...others}
                        tooltipPlacement={"top"}
                        tooltipSx={{
                          marginLeft: 0,
                          width: 200,
                        }}
                        options={FeeCalculatorPortfolioLevelOptions}
                        value={watchedPortfolioLevel}
                        onChange={(_, value) => {
                          if (!value) return;
                          others.onChange(value);
                          isNoPortfolioLevel && resetBalanceFields();
                        }}
                      />
                    );
                  }}
                />
              </SHStack>
            </FieldGroup>
            <SHDivider />

            <FieldGroup
              title="Which of the following will be held through the platform?"
              subTitle={
                "Please select all the different solutions you expect your client(s) to hold. This will determine which fees we include in our modelling."
              }
              maxWidth={490}
            >
              <SHStack
                spacing={3}
                direction="column"
                sx={{ width: { xs: "100%", md: 520 } }}
              >
                <SHBox>
                  {ChooseOptions.map((variableName) => {
                    const field = watchField(variableName);
                    const disabled =
                      isDisabled ||
                      (variableName === AnalysisVariable.Choose_MA &&
                        [
                          FeeCalculatorPortfolioLevel.Account,
                          FeeCalculatorPortfolioLevel.Family,
                        ].includes(
                          watchedPortfolioLevel as FeeCalculatorPortfolioLevel,
                        ));

                    return (
                      <CalculatorField
                        key={variableName}
                        control={control}
                        feeCalculator={field}
                        name={nameOf(variableName).value}
                        disabled={disabled}
                        onChange={(value) => {
                          if (value) return;

                          const isValid =
                            portfolioInvestmentTypeCheckboxValidation(
                              variableName,
                              watchedPortfolioLevel as FeeCalculatorPortfolioLevel,
                              portfolios,
                              portfolioSetups,
                              true,
                            );

                          if (isValid) {
                            clearDataConditions
                              .get(variableName)
                              ?.forEach((name) => {
                                setValue(nameOf(name).value, "0", {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                              });
                          } else {
                            setValue(nameOf(variableName).value, "true");
                            notify(
                              `At least one portfolio has an allocation to ${
                                labelMapping.get(variableName)?.name
                              }, set this to 0% to remove.`,
                              {
                                variant: "error",
                                close: true,
                              },
                            );
                          }
                        }}
                      />
                    );
                  })}
                </SHBox>
              </SHStack>
            </FieldGroup>

            {showInvestmentClassSection && (
              <>
                <SHDivider />
                <FieldGroup
                  title="Across all accounts, approximately how much do you expect will be held in each investment class?"
                  subTitle={`<p>
              Please estimate the average total holding size for the next 12
              months.
            </p>
            <br />
            <p>
              For non-managed account options, specify the amount that will
              be held <strong><u>outside</u></strong> managed accounts only. For example, if there
              are managed funds held both outside and inside managed
              accounts, in the ‘Managed funds’ field only enter the value
              that is held outside managed accounts.
            </p>
            <br />
            <p>
              Portfolios are assumed to be held in the same allocation by all accounts in the family. 
              We will pro rata the fees charged for holding the non-cash
              investments across your client's IDPS and Super accounts. If
              you leave these fields blank, we will not include the cost of
              holding these investments in our fee estimates.
            </p>`}
                  maxWidth={490}
                >
                  <SHStack
                    direction="row"
                    rowGap={3}
                    columnGap={2}
                    flexWrap="wrap"
                    sx={{ width: { xs: "100%", md: 520 } }}
                  >
                    {hasIDPS && (
                      <CalculatorField
                        control={control}
                        disabled={isDisabled}
                        feeCalculator={watchField(
                          AnalysisVariable.IDPS_Cash_balance,
                        )}
                        name={nameOf(AnalysisVariable.IDPS_Cash_balance).value}
                      />
                    )}
                    {hasSuper && (
                      <CalculatorField
                        control={control}
                        disabled={isDisabled}
                        feeCalculator={watchField(
                          AnalysisVariable.Super_Cash_balance,
                        )}
                        name={nameOf(AnalysisVariable.Super_Cash_balance).value}
                      />
                    )}
                    {isChooseMF && (
                      <CalculatorField
                        control={control}
                        disabled={isDisabled}
                        feeCalculator={watchField(
                          AnalysisVariable.MF_balance_i,
                        )}
                        name={nameOf(AnalysisVariable.MF_balance_i).value}
                      />
                    )}
                    {isChooseMA && (
                      <CalculatorField
                        control={control}
                        disabled={isDisabled}
                        feeCalculator={watchField(
                          AnalysisVariable.MA_balance_i,
                        )}
                        name={nameOf(AnalysisVariable.MA_balance_i).value}
                      />
                    )}
                    {isChooseAU && (
                      <CalculatorField
                        control={control}
                        disabled={isDisabled}
                        feeCalculator={watchField(
                          AnalysisVariable.AU_balance_i,
                        )}
                        name={nameOf(AnalysisVariable.AU_balance_i).value}
                      />
                    )}
                    {isChooseIntl && (
                      <CalculatorField
                        control={control}
                        disabled={isDisabled}
                        feeCalculator={watchField(
                          AnalysisVariable.Intl_balance_i,
                        )}
                        name={nameOf(AnalysisVariable.Intl_balance_i).value}
                      />
                    )}
                    {isChooseTD && (
                      <CalculatorField
                        control={control}
                        disabled={isDisabled}
                        feeCalculator={watchField(
                          AnalysisVariable.TD_balance_i,
                        )}
                        name={nameOf(AnalysisVariable.TD_balance_i).value}
                      />
                    )}
                    {isChooseBond && (
                      <CalculatorField
                        control={control}
                        disabled={isDisabled}
                        feeCalculator={watchField(
                          AnalysisVariable.Bond_balance_i,
                        )}
                        name={nameOf(AnalysisVariable.Bond_balance_i).value}
                      />
                    )}
                    <SHStack spacing={"5px"} width="100%">
                      <Collapse in={!isValidTotal && fetchedData}>
                        <SHAlert
                          severity="error"
                          textAlign={"left"}
                          icon={<WarningSVG />}
                        >
                          Total balance in this section exceeds the total
                          account balances specified above
                        </SHAlert>
                      </Collapse>
                      <Collapse in={!isValidIDPS && fetchedData}>
                        <SHAlert
                          severity="error"
                          textAlign={"left"}
                          icon={<WarningSVG />}
                        >
                          Cash balance for IDPS accounts must be smaller than
                          the total balance of IDPS accounts specified above
                        </SHAlert>
                      </Collapse>
                      <Collapse in={!isValidSuper && fetchedData}>
                        <SHAlert
                          severity="error"
                          textAlign={"left"}
                          icon={<WarningSVG />}
                        >
                          Cash balance for Super/Pension accounts must be
                          smaller than the total balance of Super/Pension
                          accounts specified above
                        </SHAlert>
                      </Collapse>
                      <FamilyCardTotal
                        title={"Remaining allocation"}
                        content={
                          <>
                            {(() => {
                              const value =
                                round(calculatedTotal.total, 2) -
                                round(totalBalanceInvestmentClassSection, 2);
                              const showDecimalPlacesForValue =
                                Math.abs(value) % 1 !== 0;

                              return (
                                <NumericFormat
                                  displayType="text"
                                  defaultValue={0}
                                  value={value}
                                  thousandSeparator=","
                                  decimalScale={
                                    showDecimalPlacesForValue ? 2 : 0
                                  }
                                  fixedDecimalScale={showDecimalPlacesForValue}
                                />
                              );
                            })()}
                          </>
                        }
                      />
                    </SHStack>
                  </SHStack>
                </FieldGroup>
              </>
            )}

            {!isNoPortfolioLevel && (
              <>
                <SHDivider />
                <FieldGroup
                  title={
                    "SMA portfolio setup: Add or edit client portfolios for each platform."
                  }
                  subTitle={`<p>Portfolios are assumed to be held in the same allocation by all accounts in the family.</p>
                  <br /><p>Once you add your first portfolio, it will be named ‘Portfolio 1’. You can assign this to other platforms from the dropdown menu or you can add a new portfolio for each platform. Subsequent portfolios will be named sequentially, such as ‘Portfolio 2’.</p>
                  <br /><p>If you edit an existing portfolio, it will be saved as a new portfolio. For example, if ‘Portfolio 1’ and ‘Portfolio 2’ exist, editing ‘Portfolio 1’ will create a new version named ‘Portfolio 3’. You can still allocate the original ‘Portfolio 1’ to another platform.</p>
                  <br /><p>When you leave this page, all unassigned portfolios will be deleted.</p>`}
                  maxWidth={490}
                >
                  <AnalysisFeePortfolioSetupSection
                    analysisId={analysisId ?? "new"}
                    calculatedTotal={calculatedTotal}
                    feeCalculators={watchedFeeCalculators}
                    investmentTypeSelection={investmentTypeSelection}
                  />
                </FieldGroup>
              </>
            )}

            {/* Only show the section if CFS Edge is included in the analysis */}
            {isShowCFSEdgeSection && (
              <>
                <SHDivider />
                <FieldGroup
                  title="CFS Edge: Across all accounts, approximately how much do you expect to be held in alternate admin fee menus? (Optional)"
                  subTitle="<p>Platform admin fees on CFS Edge are reduced based on the proportion of the portfolio invested in the Accelerate menu
                  and managed funds eligible for no admin fees.</p><br /><p>Please estimate the average holding in these menus over the next 12 months.
                  If you leave these fields blank, we will not include these investments in our fee estimates.</p>"
                  maxWidth={490}
                >
                  <SHStack direction="column" gap={1}>
                    <SHStack
                      direction="row"
                      rowGap={3}
                      columnGap={2}
                      flexWrap="wrap"
                      sx={{ width: { xs: "100%", md: 520 } }}
                    >
                      {/* Full menu */}
                      <AutoCalculatedField
                        disabled
                        label={"Full menu"}
                        placeholder=""
                        value={Number(fullMenuBalance.toFixed(2))}
                        sx={{
                          width: { xs: "100%", md: 250 },
                          ...(!isValidFullMenuBalance && {
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: palette.warning.main,
                            },
                            "& .MuiInput-underline.Mui-disabled:before": {
                              borderBottomColor: palette.warning.main,
                              borderBottomStyle: "solid",
                            },
                          }),
                        }}
                      />

                      {/* Cash */}
                      <AutoCalculatedField
                        disabled
                        label={"Cash"}
                        placeholder=""
                        value={Number(totalCash.toFixed(2))}
                        sx={{ width: { xs: "100%", md: 250 } }}
                      />

                      {/* Accelerate 100 SMAs */}
                      {isShowCFSEdgeA100PortfolioBalance && (
                        <CalculatorField
                          control={control}
                          disabled={isDisabled}
                          feeCalculator={watchField(
                            AnalysisVariable.Edge_A100_Portfolio_balance,
                          )}
                          name={
                            nameOf(AnalysisVariable.Edge_A100_Portfolio_balance)
                              .value
                          }
                        />
                      )}

                      {/* Accelerate 30 SMAs */}
                      {isShowCFSEdgeA30PortfolioBalance && (
                        <CalculatorField
                          control={control}
                          disabled={isDisabled}
                          feeCalculator={watchField(
                            AnalysisVariable.Edge_A30_Portfolio_balance,
                          )}
                          name={
                            nameOf(AnalysisVariable.Edge_A30_Portfolio_balance)
                              .value
                          }
                        />
                      )}

                      {/* No admin fee managed funds */}
                      {isShowCFSEdgeEligibleMFBalance && (
                        <CalculatorField
                          control={control}
                          disabled={isDisabled}
                          feeCalculator={watchField(
                            AnalysisVariable.Edge_eligible_MF_balance,
                          )}
                          name={
                            nameOf(AnalysisVariable.Edge_eligible_MF_balance)
                              .value
                          }
                        />
                      )}
                    </SHStack>

                    <SHStack paddingTop={2} gap={1}>
                      <Collapse in={!isValidEdeTotalA100A30Balance}>
                        <SHAlert
                          width={520}
                          severity="error"
                          textAlign={"left"}
                          icon={<WarningSVG />}
                        >
                          The total amount entered for "
                          {
                            labelMapping.get(
                              AnalysisVariable.Edge_A100_Portfolio_balance,
                            )?.name
                          }
                          " and "
                          {
                            labelMapping.get(
                              AnalysisVariable.Edge_A30_Portfolio_balance,
                            )?.name
                          }
                          " cannot exceed the amount specified above for “
                          {
                            labelMapping.get(AnalysisVariable.MA_balance_i)
                              ?.name
                          }
                          ”
                        </SHAlert>
                      </Collapse>

                      <Collapse in={!isValidEdgeEligibleMFBalance}>
                        <SHAlert
                          width={520}
                          severity="error"
                          textAlign={"left"}
                          icon={<WarningSVG />}
                        >
                          The amount entered for "
                          {
                            labelMapping.get(
                              AnalysisVariable.Edge_eligible_MF_balance,
                            )?.name
                          }
                          " cannot exceed the amount specified above for “
                          {
                            labelMapping.get(AnalysisVariable.MF_balance_i)
                              ?.name
                          }
                          ”
                        </SHAlert>
                      </Collapse>

                      <Collapse in={!isValidFullMenuBalance}>
                        <SHAlert
                          severity="error"
                          textAlign={"left"}
                          icon={<WarningSVG />}
                        >
                          Holding size cannot be negative
                        </SHAlert>
                      </Collapse>
                    </SHStack>
                  </SHStack>
                </FieldGroup>
              </>
            )}

            {/* Only show the section if North is included in the analysis */}
            {isShowNorthSection && (
              <>
                <SHDivider />
                <FieldGroup
                  title="North: Across all accounts, approximately how much do you expect to be held in alternate admin fee menus? (Optional)"
                  subTitle="<p>North platform admin fees are reduced based on the proportion of the portfolio invested in the Select and Grow menus.</p><br />
                  <p>Please estimate the average holding in this menu over the next 12 months. If you leave these fields blank, we will not include these investments in our fee estimates.</p>"
                  maxWidth={490}
                >
                  <SHStack direction="column" gap={1}>
                    <SHStack
                      direction="row"
                      rowGap={3}
                      columnGap={2}
                      flexWrap="wrap"
                      sx={{ width: { xs: "100%", md: 520 } }}
                    >
                      {/* Full menu */}
                      <AutoCalculatedField
                        disabled
                        label={"Choice menu"}
                        placeholder=""
                        value={Number(choiceMenuBalance.toFixed(2))}
                        sx={{
                          width: { xs: "100%", md: 250 },
                          ...(!isValidChoiceMenuBalance && {
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: palette.warning.main,
                            },
                            "& .MuiInput-underline.Mui-disabled:before": {
                              borderBottomColor: palette.warning.main,
                              borderBottomStyle: "solid",
                            },
                          }),
                        }}
                      />
                      {/* Cash and term deposits */}
                      <AutoCalculatedField
                        disabled
                        label={"Cash and term deposits"}
                        placeholder=""
                        value={Number(totalCashAndTD.toFixed(2))}
                        sx={{ width: { xs: "100%", md: 250 } }}
                      />

                      {/* North Select */}
                      {isShowNorthSelectBalance && (
                        <CalculatorField
                          control={control}
                          disabled={isDisabled}
                          feeCalculator={watchField(
                            AnalysisVariable.NorthSelect_balance,
                          )}
                          name={
                            nameOf(AnalysisVariable.NorthSelect_balance).value
                          }
                        />
                      )}

                      {/* North Grow menu */}
                      {isShowNorthGrowBalance && (
                        <CalculatorField
                          control={control}
                          disabled={isDisabled}
                          feeCalculator={watchField(
                            AnalysisVariable.North_Grow_Portfolio_balance,
                          )}
                          name={
                            nameOf(
                              AnalysisVariable.North_Grow_Portfolio_balance,
                            ).value
                          }
                        />
                      )}
                    </SHStack>

                    <SHStack paddingTop={2} gap={1}>
                      <Collapse in={!isValidNorthSelectBalance}>
                        <SHAlert
                          width={520}
                          severity="error"
                          textAlign={"left"}
                          icon={<WarningSVG />}
                        >
                          The amount entered for "
                          {
                            labelMapping.get(
                              AnalysisVariable.NorthSelect_balance,
                            )?.name
                          }
                          " cannot exceed the amount specified above for “
                          {
                            labelMapping.get(AnalysisVariable.MF_balance_i)
                              ?.name
                          }
                          ” and "
                          {
                            labelMapping.get(AnalysisVariable.MA_balance_i)
                              ?.name
                          }
                          "
                        </SHAlert>
                      </Collapse>
                      <Collapse in={!isValidChoiceMenuBalance}>
                        <SHAlert
                          severity="error"
                          textAlign={"left"}
                          icon={<WarningSVG />}
                        >
                          Holding size cannot be negative
                        </SHAlert>
                      </Collapse>
                    </SHStack>
                  </SHStack>
                </FieldGroup>
              </>
            )}

            {/* Only show the section if Macquarie Wrap is included in the analysis */}
            {isShowHoldingsNumber && (
              <>
                <SHDivider />
                <FieldGroup
                  title="Macquarie Manager: How many different funds, listed investments and managed account models will be held across all accounts?"
                  subTitle="Please estimate the total number of holdings. For example, if you have a portfolio with eight managed accounts plus shares in one company, the value will be 9. The figure you provide will affect the fee tiering used by some platforms. If you leave these fields blank, some admin fees may appear slightly lower than they actually are"
                  maxWidth={490}
                >
                  <SHStack
                    direction="row"
                    rowGap={3}
                    columnGap={2}
                    flexWrap="wrap"
                    sx={{ width: { xs: "100%", md: 520 } }}
                  >
                    <CalculatorField
                      control={control}
                      disabled={isDisabled}
                      feeCalculator={watchField(
                        AnalysisVariable.Holdings_number,
                      )}
                      name={nameOf(AnalysisVariable.Holdings_number).value}
                    />
                  </SHStack>
                </FieldGroup>
              </>
            )}

            {showTransactionOutsideSection && (
              <>
                <SHDivider />
                <FieldGroup
                  title={`In the next 12 months, what is the total number of transactions you expect <u>outside</u> SMAs/MDAs, and what is your anticipated average transaction size?`}
                  subTitle={`<p>Please count all transactions that you expect will happen <u>outside</u> the managed account structure. This includes any ad hoc trades you place plus any transactions from automatic buy, sell and rebalancing facilities. We will use this to estimate the transaction fees charged. If you leave these fields blank, we will not include transaction fees in our fee estimates.</p>`}
                  maxWidth={490}
                  paddingBottom={1}
                >
                  <SHStack
                    spacing={3}
                    direction="column"
                    sx={{ width: { xs: "100%", md: 520 } }}
                  >
                    {isChooseMF && (
                      <SHStack
                        spacing={2}
                        direction="row"
                        alignItems={"flex-end"}
                      >
                        <CalculatorField
                          control={control}
                          disabled={isDisabled}
                          feeCalculator={watchField(
                            AnalysisVariable.MF_transactions,
                          )}
                          name={nameOf(AnalysisVariable.MF_transactions).value}
                        />
                        <CalculatorField
                          control={control}
                          disabled={isDisabled}
                          feeCalculator={watchField(AnalysisVariable.MF_avg)}
                          name={nameOf(AnalysisVariable.MF_avg).value}
                        />
                      </SHStack>
                    )}
                    {isChooseAU && (
                      <SHStack
                        spacing={2}
                        direction="row"
                        alignItems={"flex-end"}
                      >
                        <CalculatorField
                          control={control}
                          disabled={isDisabled}
                          feeCalculator={watchField(
                            AnalysisVariable.AU_transactions,
                          )}
                          name={nameOf(AnalysisVariable.AU_transactions).value}
                        />
                        <CalculatorField
                          control={control}
                          disabled={isDisabled}
                          feeCalculator={watchField(AnalysisVariable.AU_avg)}
                          name={nameOf(AnalysisVariable.AU_avg).value}
                        />
                      </SHStack>
                    )}
                    {isChooseIntl && (
                      <SHStack
                        spacing={2}
                        direction="row"
                        alignItems={"flex-end"}
                      >
                        <CalculatorField
                          control={control}
                          disabled={isDisabled}
                          feeCalculator={watchField(
                            AnalysisVariable.Intl_transactions,
                          )}
                          name={
                            nameOf(AnalysisVariable.Intl_transactions).value
                          }
                        />
                        <CalculatorField
                          control={control}
                          disabled={isDisabled}
                          feeCalculator={watchField(AnalysisVariable.Intl_avg)}
                          name={nameOf(AnalysisVariable.Intl_avg).value}
                        />
                      </SHStack>
                    )}
                    {isChooseBond && (
                      <SHStack
                        spacing={2}
                        direction="row"
                        alignItems={"flex-end"}
                      >
                        <CalculatorField
                          control={control}
                          disabled={isDisabled}
                          feeCalculator={watchField(
                            AnalysisVariable.Bond_transactions,
                          )}
                          name={
                            nameOf(AnalysisVariable.Bond_transactions).value
                          }
                        />
                        <CalculatorField
                          control={control}
                          disabled={isDisabled}
                          feeCalculator={watchField(AnalysisVariable.Bond_avg)}
                          name={nameOf(AnalysisVariable.Bond_avg).value}
                        />
                      </SHStack>
                    )}
                  </SHStack>
                </FieldGroup>
              </>
            )}

            {showTransactionWithinSection && (
              <>
                <SHDivider />
                <FieldGroup
                  title={`<p>In the next 12 months, what is the total number of transactions you expect <u>within</u> the SMAs/MDAs your client holds, and what is your anticipated average transaction size?</p>`}
                  subTitle={`<p>Please count all transactions that you expect will happen <u>within</u> the managed account structure. Most platforms have a different fee for transactions that occur within managed accounts than for those outside managed accounts. This can be difficult for you to estimate, as each manager varies in their trading activity. If you leave these fields blank, we will not include managed account transaction fees in our fee estimates.</p>`}
                  maxWidth={490}
                >
                  <SHStack
                    spacing={3}
                    direction="column"
                    sx={{ width: { xs: "100%", md: 520 } }}
                  >
                    <SHStack
                      spacing={2}
                      direction="row"
                      alignItems={"flex-end"}
                    >
                      <CalculatorField
                        control={control}
                        disabled={isDisabled}
                        feeCalculator={watchField(
                          AnalysisVariable.MA_MF_transactions,
                        )}
                        name={nameOf(AnalysisVariable.MA_MF_transactions).value}
                      />
                      <CalculatorField
                        control={control}
                        disabled={isDisabled}
                        feeCalculator={watchField(AnalysisVariable.MA_MF_avg)}
                        name={nameOf(AnalysisVariable.MA_MF_avg).value}
                      />
                    </SHStack>
                    <SHStack
                      spacing={2}
                      direction="row"
                      alignItems={"flex-end"}
                    >
                      <CalculatorField
                        control={control}
                        disabled={isDisabled}
                        feeCalculator={watchField(
                          AnalysisVariable.MA_AU_transactions,
                        )}
                        name={nameOf(AnalysisVariable.MA_AU_transactions).value}
                      />
                      <CalculatorField
                        control={control}
                        disabled={isDisabled}
                        feeCalculator={watchField(AnalysisVariable.MA_AU_avg)}
                        name={nameOf(AnalysisVariable.MA_AU_avg).value}
                      />
                    </SHStack>
                    <SHStack
                      spacing={2}
                      direction="row"
                      alignItems={"flex-end"}
                    >
                      <CalculatorField
                        control={control}
                        disabled={isDisabled}
                        feeCalculator={watchField(
                          AnalysisVariable.MA_Intl_transactions,
                        )}
                        name={
                          nameOf(AnalysisVariable.MA_Intl_transactions).value
                        }
                      />
                      <CalculatorField
                        control={control}
                        disabled={isDisabled}
                        feeCalculator={watchField(AnalysisVariable.MA_Intl_avg)}
                        name={nameOf(AnalysisVariable.MA_Intl_avg).value}
                      />
                    </SHStack>
                  </SHStack>
                </FieldGroup>
              </>
            )}
            {isChooseRetIns && (hasIDPS || hasSuper) && (
              <>
                <SHDivider />
                <FieldGroup
                  title="The retail insurance policies that will be held through platform accounts:"
                  subTitle="Some platforms charge an admin fee for holding insurance. If you leave these fields blank, we will not include the admin fees on insurance in our fee estimates."
                  maxWidth={490}
                >
                  <SHStack
                    spacing={3}
                    direction="column"
                    sx={{ width: { xs: "100%", md: 520 } }}
                  >
                    {hasIDPS && (
                      <SHStack
                        spacing={2}
                        direction="row"
                        alignItems={"flex-end"}
                      >
                        <CalculatorField
                          control={control}
                          feeCalculator={watchField(
                            AnalysisVariable.IDPS_num_ret_Ins,
                          )}
                          name={nameOf(AnalysisVariable.IDPS_num_ret_Ins).value}
                          disabled={isDisabled}
                        />
                      </SHStack>
                    )}
                    {hasSuper && (
                      <SHStack
                        spacing={2}
                        direction="row"
                        alignItems={"flex-end"}
                      >
                        <CalculatorField
                          control={control}
                          feeCalculator={watchField(
                            AnalysisVariable.Super_num_ret_Ins,
                          )}
                          name={
                            nameOf(AnalysisVariable.Super_num_ret_Ins).value
                          }
                          disabled={isDisabled}
                        />
                        <CalculatorField
                          control={control}
                          feeCalculator={watchField(
                            AnalysisVariable.Premium_avg,
                          )}
                          name={nameOf(AnalysisVariable.Premium_avg).value}
                          disabled={isDisabled}
                        />
                      </SHStack>
                    )}
                  </SHStack>
                </FieldGroup>
              </>
            )}
          </SHStack>
        </SHContainer>
        <FeeCalculatorDisclaimerDialog
          open={showFeeDialog}
          data={disclaimers}
          onClose={() => setShowFeeDialog(false)}
        />
        {confirmDialog !== undefined && (
          <NoBalancesDialog
            onClose={() => confirmDialog.callback(false)}
            onSubmit={() => {
              confirmDialog.callback(true);
            }}
          />
        )}
      </form>
      <InvalidPortfolioWarningDialog
        open={isOpenInvalidPortfolioWarningDialog}
        onReturn={toggleIsOpenInvalidPortfolioWarningDialog}
      />
    </FormProvider>
  );
};

interface CalculatorFieldProps {
  feeCalculator?: FeeCalculatorDTO;
  control: Control<AnalysisFeeCalculatorDTO, any>;
  name: FieldPath<AnalysisFeeCalculatorDTO>;
  disabled?: boolean;
  onChange?: (value: any) => void;
}

export const CalculatorField = ({
  control,
  name,
  disabled,
  feeCalculator,
  onChange: onChangeProp,
}: CalculatorFieldProps) => {
  if (!feeCalculator) return null;
  const { fieldType, name: label, defaultValue } = feeCalculator;
  if (fieldType === FeeCalculatorFieldType.Checkbox)
    return (
      <Controller
        name={name}
        control={control}
        render={({ field: { ref, value, onChange, ...others } }) => (
          <SHBox>
            <SHFormControlLabel
              {...others}
              value={null}
              control={<SHCheckbox value={""} />}
              label={label}
              checked={value === "true"}
              disabled={disabled}
              onChange={(event: any) => {
                onChange(event.target.checked ? "true" : "false");
                if (onChangeProp) onChangeProp(event.target.checked);
              }}
            />
          </SHBox>
        )}
      />
    );
  if (fieldType === FeeCalculatorFieldType.Numeric)
    return (
      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange, ref, ...others } }) => (
          <SHNumericField
            {...others}
            numericFormatProps={{
              thousandSeparator: true,
              decimalScale: 0,
            }}
            placeholder=""
            label={label}
            disabled={disabled}
            min={0}
            value={!value ? 0 : Number(value)}
            sx={{ width: { xs: "100%", md: 250 } }}
            onChange={(_, value) => {
              onChange(value ? value.toString() : defaultValue);
              if (onChangeProp) onChangeProp(value);
            }}
          />
        )}
      />
    );

  if (fieldType === FeeCalculatorFieldType.Currency)
    return (
      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange, ref, ...others } }) => (
          <FeeCurrencyField
            {...others}
            label={label}
            placeholder=""
            disabled={disabled}
            value={!value ? 0 : Number(value)}
            sx={{ width: { xs: "100%", md: 250 } }}
            onChange={(_, value) => {
              onChange(value ? value.toString() : defaultValue);
              if (onChangeProp) onChangeProp(value);
            }}
          />
        )}
      />
    );
  return null;
};
